import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const CustomerAuthContext = createContext(null);
const API = process.env.REACT_APP_API_URL || 'http://localhost:8002/api/v1';
const TK  = 'watrly_customer_token';
const RTK = 'watrly_customer_refresh_token';
const CK  = 'watrly_customer_data';

export const CustomerAuthProvider = ({ children }) => {
  const [customer, setCustomer] = useState(null);
  const [loading,  setLoading]  = useState(true);

  const getToken     = ()  => localStorage.getItem(TK);
  const setTokens    = (a, r) => { localStorage.setItem(TK, a); if (r) localStorage.setItem(RTK, r); };
  const clearStorage = () => { [TK, RTK, CK].forEach(k => localStorage.removeItem(k)); };

  const apiCall = useCallback(async (endpoint, options = {}) => {
    const token = getToken();
    const res   = await fetch(`${API}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...options.headers,
      },
    });
    if (!res.ok) {
      if (res.status === 401) { clearStorage(); setCustomer(null); }
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'حدث خطأ غير متوقع');
    }
    return res.json();
  }, []);

  const login = async (phone, password) => {
    const data = await apiCall('/customer/auth/login', {
      method: 'POST',
      body: JSON.stringify({ phone, password }),
    });
    setTokens(data.access_token, data.refresh_token);
    setCustomer(data.customer);
    localStorage.setItem(CK, JSON.stringify(data.customer));
    return data.customer;
  };

  const register = async (payload) => {
    const data = await apiCall('/customer/auth/register', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    setTokens(data.access_token, data.refresh_token);
    setCustomer(data.customer);
    localStorage.setItem(CK, JSON.stringify(data.customer));
    return data.customer;
  };

  const logout = useCallback(() => { clearStorage(); setCustomer(null); }, []);

  const refreshCustomer = useCallback(async () => {
    try {
      const data = await apiCall('/customer/auth/me');
      setCustomer(data);
      localStorage.setItem(CK, JSON.stringify(data));
      return data;
    } catch { return null; }
  }, [apiCall]);

  useEffect(() => {
    const init = async () => {
      const token = getToken();
      const saved = localStorage.getItem(CK);
      if (token && saved) {
        try {
          setCustomer(JSON.parse(saved));
          const res = await fetch(`${API}/customer/auth/me`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (res.ok) {
            const data = await res.json();
            setCustomer(data);
            localStorage.setItem(CK, JSON.stringify(data));
          } else {
            const rt = localStorage.getItem(RTK);
            if (rt) {
              const ref = await fetch(`${API}/customer/auth/refresh`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ refresh_token: rt }),
              });
              if (ref.ok) {
                const d = await ref.json();
                setTokens(d.access_token, d.refresh_token);
              } else { logout(); }
            } else { logout(); }
          }
        } catch { logout(); }
      }
      setLoading(false);
    };
    init();
  }, [logout]);

  return (
    <CustomerAuthContext.Provider value={{
      customer, loading, isAuthenticated: !!customer,
      login, register, logout, apiCall, refreshCustomer, getToken,
    }}>
      {children}
    </CustomerAuthContext.Provider>
  );
};

export const useCustomerAuth = () => {
  const ctx = useContext(CustomerAuthContext);
  if (!ctx) throw new Error('useCustomerAuth must be used within CustomerAuthProvider');
  return ctx;
};

export default CustomerAuthContext;
